import { useState, useEffect } from 'react';
import styles from '../../styles/agents.module.css';
import CollectionSelector from './CollectionSelector';
import ToolSelector from './ToolSelector';
import api from '../../API.js';

function AgentForm({ agent, onSubmit, onCancel, disabled = false }) {
    const [formData, setFormData] = useState({
        name: agent?.name || '',
        description: agent?.description || '',
        systemPrompt: agent?.systemPrompt || 'You are a helpful assistant with access to a knowledge base.\n\nUse the following context from the knowledge base to answer the user\'s question. If the context doesn\'t contain relevant information to answer the question, you may use your general knowledge to provide a helpful answer.',
        tools: agent?.tools || [],
        rag_collections: agent?.rag_collections || [],
        defaultModel: agent?.defaultModel || '',
        defaultTopK: agent?.defaultTopK || 5,
        defaultMinSimilarity: agent?.defaultMinSimilarity || 0.7,
        isActive: agent?.isActive !== undefined ? agent.isActive : true,
    });

    const [availableModels, setAvailableModels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchModels();
    }, []);

    const fetchModels = async () => {
        try {
            const response = await fetch('/llmapi/api/tags');
            const data = await response.json();
            if (data.models && data.models.length > 0) {
                const modelNames = data.models.map(m => m.name);
                setAvailableModels(modelNames);
                // Set default if not set
                if (!formData.defaultModel && modelNames.length > 0) {
                    const llmModels = modelNames.filter(m => 
                        !m.includes('embed') && 
                        !m.includes('embedding') &&
                        !m.startsWith('nomic-embed')
                    );
                    if (llmModels.length > 0) {
                        setFormData(prev => ({ ...prev, defaultModel: llmModels[0] }));
                    } else if (modelNames.length > 0) {
                        setFormData(prev => ({ ...prev, defaultModel: modelNames[0] }));
                    }
                }
            }
        } catch (err) {
            console.error('Error fetching models:', err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // Validate required fields
            if (!formData.name.trim()) {
                throw new Error('Agent name is required');
            }
            if (!formData.systemPrompt.trim()) {
                throw new Error('System prompt is required');
            }

            // Convert rag_collections to RAG tool if tools array is empty but collections exist
            let tools = formData.tools || [];
            if (tools.length === 0 && formData.rag_collections && formData.rag_collections.length > 0) {
                tools = [{
                    type: 'rag',
                    config: { collections: formData.rag_collections }
                }];
            }

            // Prepare data for submission
            const submitData = {
                name: formData.name.trim(),
                description: formData.description.trim(),
                systemPrompt: formData.systemPrompt.trim(),
                tools: tools,
                rag_collections: formData.rag_collections, // Keep for backwards compat
                defaultModel: formData.defaultModel || 'llama3.2',
                defaultTopK: formData.defaultTopK,
                defaultMinSimilarity: formData.defaultMinSimilarity,
                isActive: formData.isActive
            };

            await onSubmit(submitData);
        } catch (err) {
            setError(err.message || 'Failed to save agent');
        } finally {
            setLoading(false);
        }
    };

    const handleCollectionsChange = (collections) => {
        setFormData(prev => ({ ...prev, rag_collections: collections }));
        
        // Update RAG tool config if it exists
        const ragToolIndex = formData.tools.findIndex(t => t.type === 'rag');
        if (ragToolIndex >= 0) {
            const updatedTools = [...formData.tools];
            updatedTools[ragToolIndex] = {
                ...updatedTools[ragToolIndex],
                config: { ...updatedTools[ragToolIndex].config, collections }
            };
            setFormData(prev => ({ ...prev, tools: updatedTools }));
        }
    };

    return (
        <form onSubmit={handleSubmit} className={styles.form}>
            {error && (
                <div className={styles.error}>
                    {error}
                </div>
            )}

            <div className={styles.formSection}>
                <h3 className={styles.formSectionTitle}>Basic Information</h3>
                
                <div className={styles.formGroup}>
                    <label className={`${styles.formLabel} ${styles.formLabelRequired}`}>
                        Agent Name
                    </label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        required
                        disabled={disabled}
                        className={styles.formInput}
                        placeholder="e.g., Research Assistant"
                    />
                </div>

                {agent && agent.agentId && (
                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>
                            Agent ID
                        </label>
                        <input
                            type="text"
                            value={agent.agentId}
                            disabled
                            readOnly
                            className={styles.formInput}
                            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                        <div className={styles.formHelp} style={{ color: '#666', fontStyle: 'italic' }}>
                            Agent ID cannot be changed once set. Changing it would break workflows that reference this agent.
                        </div>
                    </div>
                )}

                <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                        Description
                    </label>
                    <input
                        type="text"
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        disabled={disabled}
                        className={styles.formInput}
                        placeholder="Brief description of what this agent does"
                    />
                </div>
            </div>

            <div className={styles.formSection}>
                <h3 className={styles.formSectionTitle}>System Prompt</h3>
                
                <div className={styles.formGroup}>
                    <label className={`${styles.formLabel} ${styles.formLabelRequired}`}>
                        System Prompt
                    </label>
                    <textarea
                        value={formData.systemPrompt}
                        onChange={(e) => setFormData(prev => ({ ...prev, systemPrompt: e.target.value }))}
                        required
                        disabled={disabled}
                        className={`${styles.formInput} ${styles.formTextarea}`}
                        rows={10}
                    />
                    <div className={styles.formHelp}>
                        {formData.systemPrompt.length} characters
                    </div>
                </div>
            </div>

            <div className={styles.formSection}>
                <h3 className={styles.formSectionTitle}>Knowledge Base</h3>
                
                <CollectionSelector
                    selectedCollections={formData.rag_collections}
                    onChange={handleCollectionsChange}
                    disabled={disabled}
                />
            </div>

            <div className={styles.formSection}>
                <h3 className={styles.formSectionTitle}>Tools</h3>
                
                <ToolSelector
                    selectedTools={formData.tools}
                    onChange={(tools) => setFormData(prev => ({ ...prev, tools }))}
                    disabled={disabled}
                />
            </div>

            <div className={styles.formSection}>
                <h3 className={styles.formSectionTitle}>Model Configuration</h3>
                
                <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                        Default Model
                    </label>
                    {availableModels.length > 0 ? (
                        <select
                            value={formData.defaultModel}
                            onChange={(e) => setFormData(prev => ({ ...prev, defaultModel: e.target.value }))}
                            disabled={disabled}
                            className={styles.formInput}
                        >
                            <option value="">Select a model...</option>
                            {availableModels
                                .filter(m => !m.includes('embed') && !m.includes('embedding') && !m.startsWith('nomic-embed'))
                                .map(model => (
                                    <option key={model} value={model}>{model}</option>
                                ))}
                        </select>
                    ) : (
                        <input
                            type="text"
                            value={formData.defaultModel}
                            onChange={(e) => setFormData(prev => ({ ...prev, defaultModel: e.target.value }))}
                            disabled={disabled}
                            className={styles.formInput}
                            placeholder="e.g., llama3.2"
                        />
                    )}
                    <div className={styles.formHelp}>
                        Default LLM model to use for this agent
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1em' }}>
                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>
                            Top K
                        </label>
                        <input
                            type="number"
                            min="1"
                            max="20"
                            value={formData.defaultTopK}
                            onChange={(e) => setFormData(prev => ({ ...prev, defaultTopK: parseInt(e.target.value) || 5 }))}
                            disabled={disabled}
                            className={styles.formInput}
                        />
                        <div className={styles.formHelp}>
                            Number of documents to retrieve
                        </div>
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>
                            Min Similarity
                        </label>
                        <input
                            type="number"
                            min="0"
                            max="1"
                            step="0.1"
                            value={formData.defaultMinSimilarity}
                            onChange={(e) => setFormData(prev => ({ ...prev, defaultMinSimilarity: parseFloat(e.target.value) || 0.7 }))}
                            disabled={disabled}
                            className={styles.formInput}
                        />
                        <div className={styles.formHelp}>
                            Minimum similarity threshold (0-1)
                        </div>
                    </div>
                </div>

                <div className={styles.formGroup}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5em', cursor: disabled ? 'not-allowed' : 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={formData.isActive}
                            onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                            disabled={disabled}
                        />
                        <span>Agent is active</span>
                    </label>
                </div>
            </div>

            <div style={{ display: 'flex', gap: '1em', marginTop: '2em' }}>
                <button
                    type="submit"
                    disabled={loading || disabled}
                    className={`${styles.button} ${styles.buttonPrimary}`}
                >
                    {loading ? 'Saving...' : (agent ? 'Update Agent' : 'Create Agent')}
                </button>
                {onCancel && (
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={loading || disabled}
                        className={`${styles.button} ${styles.buttonSecondary}`}
                    >
                        Cancel
                    </button>
                )}
            </div>
        </form>
    );
}

export default AgentForm;
